import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, FileSpreadsheet, ShieldCheck, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AuthShowcase from "@/components/AuthShowcase";
import { track, getReferral } from "@/lib/track";

const inputCls = "h-11 rounded-xl bg-white/[0.04] border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-[#D4C3A3] focus-visible:ring-offset-0";

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("mode") === "signup" ? "signup" : "login";
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({ name: "", email: "", password: "" });
  const [forgotEmail, setForgotEmail] = useState("");

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (defaultTab === "signup") track("signup_started", { from: "url" });
  }, [defaultTab]);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/login", loginForm);
      setUser(res.data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Login failed"));
    } finally { setBusy(false); }
  };

  const doSignup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/register", { ...signupForm, ref: getReferral() });
      setUser(res.data);
      toast.success("Account created!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Sign up failed"));
    } finally { setBusy(false); }
  };

  const doForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("Check your inbox", { description: "If an account exists, a reset link is on its way." });
      if (res.data.dev_reset_link) {
        toast.message("Dev reset link", { description: res.data.dev_reset_link, duration: 12000 });
      }
      setForgot(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050505] text-zinc-100 noise-overlay">
      {/* LEFT — form */}
      <div className="relative flex flex-col justify-center px-8 sm:px-16 lg:px-20 py-12 overflow-hidden">
        <div className="absolute top-[-8rem] left-1/2 -translate-x-1/2 h-[32rem] w-[40rem] max-w-full bg-[radial-gradient(ellipse_at_top,rgba(212,195,163,0.12),transparent_60%)] pointer-events-none" />
        <div className="max-w-md w-full animate-fade-up relative">
          <button onClick={() => navigate("/")} className="flex items-center gap-2.5 mb-8" data-testid="login-logo">
            <img src="/brand-icon-gold.png" alt="Receipt Republic" className="h-10 w-10 object-contain rounded-lg" />
            <span className="font-display font-extrabold text-xl tracking-tight text-white">Receipt Republic</span>
          </button>

          <p className="text-[11px] uppercase tracking-[0.25em] text-[#D4C3A3] font-bold mb-3">
            Independence from tax-season chaos
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter leading-none mb-7 text-white">
            Snap a receipt.<br />We do the rest.
          </h1>

          <Button
            data-testid="google-login-button"
            onClick={googleLogin}
            variant="outline"
            className="w-full h-11 rounded-xl font-semibold border-white/15 bg-transparent text-white hover:bg-white/[0.05] hover:text-white transition-colors duration-200"
          >
            <Mail className="h-4 w-4 mr-2" /> Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[10px] text-zinc-500 uppercase tracking-[0.2em]">or use email</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          {forgot ? (
            <form onSubmit={doForgot} className="space-y-4" data-testid="forgot-form">
              <p className="text-sm text-zinc-400">Enter your email and we'll send a reset link.</p>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-zinc-300">Email</Label>
                <Input data-testid="forgot-email-input" type="email" required value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)} className={inputCls} placeholder="you@example.com" />
              </div>
              <Button data-testid="forgot-submit-button" type="submit" disabled={busy} className="w-full h-11 rounded-xl bg-[#D4C3A3] text-black hover:bg-[#E5D6B8] font-semibold">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
              </Button>
              <button type="button" onClick={() => setForgot(false)} className="text-sm text-[#D4C3A3] hover:text-[#E5D6B8] w-full text-center">
                Back to sign in
              </button>
            </form>
          ) : (
            <Tabs defaultValue={defaultTab} onValueChange={(v) => { if (v === "signup") track("signup_started", { from: "tab" }); }} className="w-full">
              <TabsList className="grid grid-cols-2 w-full mb-4 bg-white/[0.04] border border-white/10 p-1 rounded-xl">
                <TabsTrigger value="login" data-testid="tab-login" className="rounded-lg text-zinc-400 data-[state=active]:bg-[#D4C3A3] data-[state=active]:text-black data-[state=active]:shadow-none font-semibold">Sign in</TabsTrigger>
                <TabsTrigger value="signup" data-testid="tab-signup" className="rounded-lg text-zinc-400 data-[state=active]:bg-[#D4C3A3] data-[state=active]:text-black data-[state=active]:shadow-none font-semibold">Create account</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={doLogin} className="space-y-4" data-testid="login-form">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-zinc-300">Email</Label>
                    <Input data-testid="login-email-input" type="email" required value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      className={inputCls} placeholder="you@icloud.com" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs font-semibold text-zinc-300">Password</Label>
                      <button type="button" onClick={() => setForgot(true)} data-testid="forgot-link" className="text-xs text-[#D4C3A3] hover:text-[#E5D6B8]">
                        Forgot?
                      </button>
                    </div>
                    <Input data-testid="login-password-input" type="password" required value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      className={inputCls} placeholder="Enter your password" />
                  </div>
                  <Button data-testid="login-submit-button" type="submit" disabled={busy} className="w-full h-11 rounded-xl bg-[#D4C3A3] text-black hover:bg-[#E5D6B8] transition-colors duration-200 font-semibold">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={doSignup} className="space-y-4" data-testid="signup-form">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-zinc-300">Name</Label>
                    <Input data-testid="signup-name-input" value={signupForm.name}
                      onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })}
                      className={inputCls} placeholder="Alex Driver" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-zinc-300">Email</Label>
                    <Input data-testid="signup-email-input" type="email" required value={signupForm.email}
                      onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
                      className={inputCls} placeholder="you@gmail.com" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-zinc-300">Password</Label>
                    <Input data-testid="signup-password-input" type="password" required value={signupForm.password}
                      onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })}
                      className={inputCls} placeholder="At least 8 characters" />
                  </div>
                  <Button data-testid="signup-submit-button" type="submit" disabled={busy} className="w-full h-11 rounded-xl bg-[#D4C3A3] text-black hover:bg-[#E5D6B8] transition-colors duration-200 font-semibold">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          )}

          <div className="grid grid-cols-3 gap-3 mt-8 text-center">
            {[
              { icon: Sparkles, label: "AI categorized" },
              { icon: FileSpreadsheet, label: "CSV export" },
              { icon: ShieldCheck, label: "20 free / mo" },
            ].map((f, i) => (
              <div key={i} className="flex flex-col items-center gap-1.5 text-xs text-zinc-500 font-medium">
                <f.icon className="h-4 w-4 text-[#D4C3A3]" />
                {f.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT — animated showcase */}
      <AuthShowcase
        badge="2026 Edition"
        quote={'"Every shoeboxed receipt is money left on the table."'}
        checks={["IRS Schedule C ready", "No bank login needed"]}
      />
    </div>
  );
}
